import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import Seo from "../components/Seo";
import VehicleCard from "../components/VehicleCard";
import LocationPicker from "../components/LocationPicker";
import { rememberSearch } from "../lib/resumeBrowsing";
import { SlidersHorizontal, Search, X, BookmarkPlus, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

const TYPE_TABS = [
  { id: "", labelEn: "All", labelAr: "الكل" },
  { id: "new", labelEn: "New", labelAr: "جديدة" },
  // iter276 — dedicated tab so any listing tagged with this type is
  // discoverable. Does NOT alter the existing "new" tab's behaviour;
  // the two tabs are independent filters on the same `type` field.
  { id: "new_customs_card", labelEn: "🆕 Customs Card", labelAr: "🆕 بطاقة جمركية" },
  { id: "used", labelEn: "Used", labelAr: "مستعملة" },
  { id: "rental", labelEn: "Rentals", labelAr: "تأجير" },
  { id: "salvage", labelEn: "Salvage", labelAr: "سكراب" },
  { id: "export", labelEn: "Export", labelAr: "تصدير" },
  { id: "motorcycle", labelEn: "Motorcycles", labelAr: "دراجات" },
  { id: "heavy", labelEn: "Heavy Equipment", labelAr: "المعدات الثقيلة" },
];

export default function MarketplacePage() {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveDone, setSaveDone] = useState(false);

  const filters = useMemo(() => ({
    type: params.get("type") || "",
    make: params.get("make") || "",
    city: params.get("city") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    q: params.get("q") || "",
    periodic_inspected: params.get("periodic_inspected") === "1" ? "1" : "",
  }), [params]);

  useEffect(() => {
    setLoading(true);
    const query = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) query[k] = v; });
    // Map the URL flag "1" to the boolean param the backend expects.
    if (filters.periodic_inspected === "1") {
      query.periodic_inspected = true;
    } else {
      delete query.periodic_inspected;
    }
    apiClient.get("/vehicles", { params: { ...query, limit: 60 } })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Remember this search for the homepage "Continue browsing" banner.
        const labelBits = [];
        if (filters.make) labelBits.push(filters.make);
        if (filters.type) labelBits.push(filters.type);
        if (filters.city) labelBits.push(filters.city);
        if (filters.q) labelBits.push(`"${filters.q}"`);
        rememberSearch({
          pathname: "/marketplace",
          search: window.location.search || "",
          label: labelBits.join(" · ") || (lang === "ar" ? "بحث في السوق" : "Marketplace search"),
        });
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [filters, lang]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
    setSaveDone(false);
  };
  const clearAll = () => { setParams({}, { replace: true }); setSaveDone(false); };

  const saveSearch = async () => {
    if (!user) { navigate("/login", { state: { from: "/marketplace" } }); return; }
    const cleanQuery = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const labelParts = [];
    if (cleanQuery.make) labelParts.push(cleanQuery.make);
    if (cleanQuery.type) labelParts.push(cleanQuery.type);
    if (cleanQuery.city) labelParts.push(`in ${cleanQuery.city}`);
    if (cleanQuery.q) labelParts.push(`"${cleanQuery.q}"`);
    const name = labelParts.join(" ") || (lang === "ar" ? "بحث محفوظ" : "Saved search");
    setSaveBusy(true);
    try {
      await apiClient.post("/saved-searches", { name, query: cleanQuery });
      setSaveDone(true);
      setTimeout(() => setSaveDone(false), 4000);
    } finally { setSaveBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="marketplace-page">
      <Seo
        title={lang === "ar" ? "سيارات للبيع في السعودية | كارتال" : "Cars for Sale in Saudi Arabia | CARTAL"}
        description={lang === "ar" ? "تصفح آلاف إعلانات السيارات الجديدة والمستعملة في السعودية — بحث ذكي بالماركة والموديل والسنة والمدينة والسعر على منصة كارتال." : "Browse thousands of new and used car listings across Saudi Arabia — smart search by make, model, year, city and price on CARTAL."}
        canonicalPath="/marketplace"
        jsonLd={(() => {
          // iter332 — ItemList of the currently-visible listings so Google
          // sees crawlable vehicle URLs from the category page. Only ids
          // are emitted; no owner data or pricing is leaked here.
          const list = (items || []).slice(0, 30).map((it, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "url": `https://cartal.sa/vehicle/${it.id}`,
          }));
          if (!list.length) return null;
          return {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": list,
            "numberOfItems": list.length,
          };
        })()}
      />
      <div className="flex items-end justify-between gap-6 flex-wrap mb-6">
        <div>
          <div className="overline mb-2">CARTAL MARKETPLACE</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">
            {lang === "ar" ? "تصفح المركبات" : "Browse Vehicles"}
          </h1>
          <p className="text-[#6b6b6b] mt-2">{total.toLocaleString()} {lang === "ar" ? "نتيجة" : "results"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={saveSearch} className="btn-ghost text-sm" disabled={saveBusy} data-testid="save-search-btn">
            {saveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : saveDone ? <CheckCircle2 className="w-4 h-4 text-[#8a6f2e]" /> : <BookmarkPlus className="w-4 h-4" />}
            {saveDone ? (lang === "ar" ? "تم الحفظ" : "Saved") : (lang === "ar" ? "احفظ البحث" : "Save search")}
          </button>
          <button onClick={() => setShowFilters((s) => !s)} className="btn-ghost text-sm" data-testid="filter-toggle">
            <SlidersHorizontal className="w-4 h-4" /> {lang === "ar" ? "الفلاتر" : "Filters"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.id || "all"}
            type="button"
            onClick={() => update("type", tab.id)}
            className={`pill ${filters.type === tab.id ? "pill-cyan" : ""} text-xs`}
            data-testid={`type-tab-${tab.id || "all"}`}
          >
            {lang === "ar" ? tab.labelAr : tab.labelEn}
          </button>
        ))}

        {/* Separator + Salama / periodic inspection toggle */}
        <span className="hidden sm:inline-block h-5 w-px bg-[#0a0a0a]/15 mx-1" aria-hidden />
        <button
          type="button"
          onClick={() => update("periodic_inspected", filters.periodic_inspected === "1" ? "" : "1")}
          className={`inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-3.5 py-1.5 border-2 transition-colors ${
            filters.periodic_inspected === "1"
              ? "bg-[#1f6f3a] text-white border-[#1f6f3a]"
              : "bg-white text-[#1f6f3a] border-[#1f6f3a]/35 hover:bg-[#1f6f3a]/5"
          }`}
          data-testid="filter-periodic-toggle"
          aria-pressed={filters.periodic_inspected === "1"}
          title={lang === "ar" ? "إظهار السيارات بفحص دوري ساري فقط" : "Show only vehicles with a valid periodic inspection"}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {lang === "ar" ? "بفحص دوري ساري" : "Periodic inspection valid"}
        </button>
      </div>

      {showFilters && (
        <div className="surface rounded-2xl p-5 mb-8 grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b]" />
            <input className="input-dark ps-10" placeholder={t("home.searchPlaceholder")} value={filters.q} onChange={(e) => update("q", e.target.value)} data-testid="filter-search" />
          </div>
          <input className="input-dark" placeholder="Make" value={filters.make} onChange={(e) => update("make", e.target.value)} data-testid="filter-make" />
          <div className="col-span-2 md:col-span-1">
            <LocationPicker
              value={filters.city}
              onChange={(id) => update("city", id || "")}
              placeholder={lang === "ar" ? "اختر المدينة أو الدولة…" : "Choose city or country…"}
              compact
              testId="filter-city-picker"
            />
          </div>
          <input className="input-dark" placeholder="Min price (SAR)" type="number" value={filters.min_price} onChange={(e) => update("min_price", e.target.value)} data-testid="filter-min-price" />
          <input className="input-dark" placeholder="Max price (SAR)" type="number" value={filters.max_price} onChange={(e) => update("max_price", e.target.value)} data-testid="filter-max-price" />
          <button onClick={clearAll} className="btn-ghost text-sm justify-center col-span-2 md:col-span-1" data-testid="filter-clear">
            <X className="w-4 h-4" /> Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="surface h-72 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="surface rounded-2xl p-12 text-center text-[#6b6b6b]" data-testid="marketplace-empty">{t("common.empty")}</div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [&>*]:break-inside-avoid [&>*]:mb-5">
          {items.map((v) => <VehicleCard key={v.id} v={v} />)}
        </div>
      )}
    </div>
  );
}
